import * as React from "react";
import { union, without } from "lodash";
import { reduxForm } from "redux-form";
import { connect } from "react-redux";
import { Button, InlineNotification, TextArea } from "carbon-components-react";
import { Attachment20, DocumentAdd20, Send32 } from "@carbon/icons-react";

import { DocTypes } from "@eave/shared/types/api";
import DocumentUploader from "../DocumentUploader";
import {
  sendMessageAsBorrower,
  sendMessageAsOperator,
  updateDocIDsForCurrentMessage,
} from "./actions";
import { TEMPLATE_PLACEHOLDER_SYMBOL, TEMPLATE_PLACEHOLDER_TEXT } from "./values";

const { Form } = require("react-bootstrap");
const FontAwesome = require("react-fontawesome");
const classnames = require("classnames");
const styles = require("./styles.module.scss");

interface MessageFormProps {
  appID: Number;
  sendMessage: Function;
  updateDocIDsForCurrentMessage: Function;
  handleSubmit: (ev) => void;
  existingDocuments?: any;
  document_ids: any;
  templateName: string;
  defaultMessage?: string;
  resetMessagesState?: Function;
  disabled?: boolean;
}

class MessageForm extends React.Component<MessageFormProps, any> {
  constructor(props: MessageFormProps) {
    super(props);

    this.state = {
      showDropzone: false,
      showDocLinker: false,
      message: props.defaultMessage,
    };

    this.handleSubmit = this.handleSubmit.bind(this);
  }

  handleSubmit() {
    const { appID, sendMessage, document_ids, resetMessagesState, templateName } = this.props;
    sendMessage(appID, this.state.message, document_ids, templateName);

    resetMessagesState();

    this.setState({
      showDropzone: false,
      showDocLinker: false,
      message: "",
    });
  }

  setDocumentIDs(docIDs) {
    const document_ids = union(this.props.document_ids, docIDs);
    this.props.updateDocIDsForCurrentMessage(this.props.appID, document_ids);
    this.setState({
      showDropzone: false,
    });
  }

  deleteDocumentID(document_id) {
    const document_ids = without(this.props.document_ids, document_id);
    this.props.updateDocIDsForCurrentMessage(this.props.appID, document_ids);
  }

  toggleAttachExistingDocument(document_id) {
    if (this.props.document_ids.indexOf(document_id) >= 0) {
      this.deleteDocumentID(document_id);
    } else {
      this.setDocumentIDs([document_id]);
    }
  }

  handleAddAttachmentClick() {
    this.setState({
      showDropzone: !this.state.showDropzone,
    });
  }

  handleAddExistingDocumentClick() {
    this.setState({
      showDocLinker: !this.state.showDocLinker,
    });
  }

  render() {
    const { disabled } = this.props;
    const dropzoneClassName = classnames({
      [styles.message_form__hide_uploader]: !this.state.showDropzone,
    });

    const uploaderPrompt = (
      <div className={styles.message_form__attachment_dropzone_prompt}>
        <span>
          <FontAwesome name="file-pdf-o" />
        </span>
        <span className={styles.message_form__attachment_dropzone__prompt_text}>
          Drag and drop or click here to upload a file
        </span>
      </div>
    );

    const messageIncludesPlaceholderText =
      this.state.message?.includes(TEMPLATE_PLACEHOLDER_TEXT) ||
      this.state.message?.includes(TEMPLATE_PLACEHOLDER_SYMBOL);

    return (
      <div className={styles.message_form}>
        {this.state.showDropzone && (
          <div
            className={styles.message_form__overlay}
            onClick={() => {
              this.setState({ showDropzone: false });
            }}
          />
        )}
        <Form onSubmit={this.props.handleSubmit(this.handleSubmit)}>
          <div className={styles.message_form__container}>
            <TextArea
              labelText="Compose new message"
              invalid={!disabled && messageIncludesPlaceholderText}
              invalidText="Message text may still contain placeholder text. Please check again and fill out any missing data."
              disabled={disabled}
              value={this.state.message}
              onChange={e => this.setState({ message: e.target.value })}
            />

            {(this.state.showDropzone || this.props.document_ids.length > 0) && (
              <DocumentUploader
                className={dropzoneClassName}
                applicationID={this.props.appID}
                documentType={DocTypes.MessageAttachment}
                prompt={uploaderPrompt}
                ownerEntityType="message"
                showOnlyCurrentUploadsOfType
                onUpload={this.setDocumentIDs.bind(this)}
                onDelete={this.deleteDocumentID.bind(this)}
              />
            )}

            <div className={styles.message_form__buttons}>
              <Button
                kind="ghost"
                onClick={this.handleAddAttachmentClick.bind(this)}
                renderIcon={Attachment20}
              >
                Add attachment
              </Button>

              <Button type="submit" renderIcon={Send32} disabled={messageIncludesPlaceholderText}>
                Send Message
              </Button>
            </div>
            {this.state.showDocLinker &&
              this.props.existingDocuments &&
              this.props.existingDocuments.length > 0 && (
                <ul>
                  {this.props.existingDocuments.map((doc, i) => (
                    <li key={i} onClick={this.toggleAttachExistingDocument.bind(this, doc.id)}>
                      <input
                        readOnly
                        type="checkbox"
                        checked={this.props.document_ids.indexOf(doc.id) >= 0}
                      />
                      <label>
                        {" "}
                        {doc.original_filename} ({doc.type})
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            {this.props.existingDocuments && this.props.existingDocuments.length > 0 && (
              <Button
                kind="ghost"
                renderIcon={DocumentAdd20}
                onClick={this.handleAddExistingDocumentClick.bind(this)}
              >
                {this.state.showDocLinker ? "Hide existing documents" : "Attach existing document"}
              </Button>
            )}
          </div>
        </Form>
      </div>
    );
  }
}

const form = reduxForm({
  form: "new-message",
  destroyOnUnmount: false,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
  sendMessage: (appID, message, document_ids, templateName) => {
    if (ownProps.isOperatorContext) {
      dispatch(sendMessageAsOperator(appID, message, document_ids, templateName));
    } else {
      dispatch(sendMessageAsBorrower(appID, message, document_ids));
    }
  },
  updateDocIDsForCurrentMessage: (appID, document_ids) => {
    dispatch(updateDocIDsForCurrentMessage(appID, document_ids));
  },
});

const mapStateToProps = (state, ownProps) => {
  const messages = state.messages[ownProps.appID];
  return {
    ...ownProps,
    document_ids: messages ? messages.documentIDsForNewMessage || [] : [],
  };
};

export default connect<any, any, any>(mapStateToProps, mapDispatchToProps)(form(MessageForm));
